import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { Doctor, DoctorBank } from 'types/doctor';
import { Bank } from 'types/bank';
import DoctorBanksList from './list/DoctorBanksList';
import DoctorBankForm from './form/DoctorBankForm';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import { countLabel, RecordToolbar } from 'components/record-shell/RecordList';

interface DoctorBanksProps {
  doctor: Doctor;
  banks: Bank[];
  handleChangeAddBank(bank: DoctorBank): void;
  handleDeleteBank(bank: DoctorBank): void;
}

const DoctorBanks: React.FC<DoctorBanksProps> = ({ doctor, handleDeleteBank, handleChangeAddBank, banks }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<DoctorBank | null>(null);
  const [bankInfo, setBankInfo] = useState<DoctorBank>({
    bank_account: '',
    bank_agency: '',
    bank_name: '',
    doctor_id: doctor.id,
    id: 0,
    pix_key: '',
    pix_key_type: '',
  });

  function handleChangeBankInfo<K extends keyof DoctorBank>(index: K, value: DoctorBank[K]) {
    setBankInfo(prev => ({ ...prev, [index]: value }));
  }

  function handleAddBank(bankInfo: DoctorBank | null) {
    if (!bankInfo) return;
    handleChangeAddBank(bankInfo);
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      doctor_id: doctor.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
    });
  }

  function onExited() {
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      doctor_id: doctor.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
    });
    setEditDialogOpen(false);
  }

  function handleEditBank(bank: DoctorBank, index: number) {
    setEditDialogOpen(true);
    setBankInfo(bank);
  }

  return (
    <Grid container size={12} spacing={2}>
      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          handleNoClick={() => setConfirmDialogOpen(null)}
          handleYesClick={() => {
            handleDeleteBank(confirmDialogOpen);
            setConfirmDialogOpen(null);
          }}
          title="Tem certeza que deseja excluir esta conta bancária?"
        />
      )}
      <Grid size={12}>
        <RecordToolbar
          title={countLabel(doctor.banks.length, 'conta bancária', 'contas bancárias', 'Nenhuma conta bancária')}
          addLabel="Adicionar conta"
          onAdd={() => setAddDialogOpen(true)}
        />
      </Grid>

      {doctor.banks.length > 0 && (
        <Grid size={12}>
          <DoctorBanksList
            banks={doctor.banks}
            handleDeleteBank={bank => setConfirmDialogOpen(bank)}
            handleEditBank={handleEditBank}
          />
        </Grid>
      )}

      {addDialogOpen && (
        <DoctorBankForm
          type="new"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}

      {!!bankInfo.id && editDialogOpen && (
        <DoctorBankForm
          type="edit"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}
    </Grid>
  );
};

export default DoctorBanks;
